/**
 * The @chamaeleonidae/chmln package bootstraps the window.chmln object
 */

import chmln from "@chamaeleonidae/chmln";

import environment from "@environment/variables";
import type { BasicUserDataFromToken } from "@covertree/auth";

import type { ChameleonUserProperties } from "./types";

export type { ChameleonUserProperties };

const isChameleonEnabled = Boolean(environment.chameleon.accountToken);

const CHAMELEON_ID_PREFIX = "chmln";

/** https://help.chameleon.io/en/articles/1226440-typing-breaking-when-using-chameleon */
const GUARDED_EVENTS = ["focusin", "keydown"] as const;

const isChameleonNode = (target: EventTarget | null): boolean => {
  let node = target as Node | null;

  while (node) {
    const { id } = node as Element;
    if (typeof id === "string" && id.startsWith(CHAMELEON_ID_PREFIX)) {
      return true;
    }
    // `host` walks out of a shadow root, whose parentNode is null
    node = node.parentNode ?? (node as ShadowRoot).host ?? null;
  }

  return false;
};

let removeChameleonEventGuard: (() => void) | null = null;

export const installChameleonEventGuard = (): (() => void) => {
  if (!isChameleonEnabled || typeof document === "undefined") return () => {};
  if (removeChameleonEventGuard) return removeChameleonEventGuard;

  const guard = (event: Event): void => {
    if (isChameleonNode(event.target)) {
      event.stopImmediatePropagation();
    }
  };

  GUARDED_EVENTS.forEach((type) => document.addEventListener(type, guard));

  removeChameleonEventGuard = () => {
    GUARDED_EVENTS.forEach((type) => document.removeEventListener(type, guard));
    removeChameleonEventGuard = null;
  };

  return removeChameleonEventGuard;
};

export const initChameleon = (): void => {
  if (!isChameleonEnabled) return;

  installChameleonEventGuard();

  chmln.init(environment.chameleon.accountToken, {
    fastUrl: "https://fast.chameleon.io/",
  });
};

export const identifyChameleonUser = (
  basicUserData: BasicUserDataFromToken,
  uidHash?: string
): void => {
  if (!isChameleonEnabled) return;

  const properties: ChameleonUserProperties = {
    ...(uidHash ? { uid_hash: uidHash } : {}),

    email: basicUserData.email,
    name: `${basicUserData.firstName} ${basicUserData.lastName}`.trim(),

    agentId: basicUserData.agentId,
    agencyId: basicUserData.agencyId,
    agencyName: basicUserData.agencyName,
    agencyType: basicUserData.agencyType,
    wholesalerId: basicUserData.wholesalerId,
    groups: basicUserData.groups,

    agentDateOfBirth: basicUserData.agentDateOfBirth,
  };

  chmln.identify(basicUserData.sub, properties);
};

export const clearChameleon = (): void => {
  if (!isChameleonEnabled) return;

  if (
    typeof window !== "undefined" &&
    typeof window.chmln?.clear === "function"
  ) {
    window.chmln.clear();
  }
};

/**
 * Register the app:navigate event listener so Chameleon can display page-targeted experiences in this SPA.
 */
export const registerChameleonNavigationListener = (
  navigate: (path: string) => void
): void => {
  if (!isChameleonEnabled) return;

  if (typeof window !== "undefined" && typeof window.chmln?.on === "function") {
    window.chmln.on("app:navigate", (opts: { to: string }) => {
      navigate(opts.to);
    });
  }
};
